import { createBrowserClient } from "@supabase/ssr";
import {
  ClientError,
  ClientStatusCodes,
  NetworkError,
  NetworkStatusCodes,
} from "../error-definitions";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { useRouter } from "../router-events";
import { LewkAPI } from "@/config/api/api-client";
import { globalCookieOptions } from "../global-utils";

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookieOptions: globalCookieOptions
    }
  );
}

export interface useAuthProps {
  isLoggedIn: boolean;
  loading: boolean;
  user: User | null;
}

export interface userSignOutProps {
  signOut: () => void;
  loading: boolean;
}
const useAuth = (): useAuthProps => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const supabase = createClient();

  const checkUser = async (): Promise<boolean> => {
    try {
      setLoading(true);

      // Step 1: Check if session exists
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError)
        throw new NetworkError(
          sessionError.message,
          sessionError.status as NetworkStatusCodes
        );

      if (!sessionData.session) {
        setIsLoggedIn(false);
        return false; // No session
      }

      setUser(sessionData.session.user);

      // Step 2: Validate the user
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError)
        throw new NetworkError(
          userError.message,
          userError.status as NetworkStatusCodes
        );

        if(userData.user) {
          setIsLoggedIn(true);
          setUser(userData.user)
        }
     
      return !!userData.user;
    } catch (error) {
      if (error instanceof NetworkError) {
        error.handle();
      } else {
        new ClientError(
          "We encountered an error with authentication. Please try again later!",
          ClientStatusCodes.UNDOCUMENTED_ERROR
        ).handle();
      }
      return false;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    checkUser();
  }, []);

  return { isLoggedIn, user, loading };
};

export const useSignOut = (): userSignOutProps => {
  const supabase = createClient();
  const [loading, setLoading] = useState<boolean>(false);
  const router = useRouter();
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();

      if (error)
        throw new NetworkError(
          error.message,
          error.status as NetworkStatusCodes
        );

      router.replace("/login");
    } catch (error) {
      if (error instanceof NetworkError) {
        error.handle();
      } else {
        new ClientError(
          "We encountered an error with authentication. Please try again later!",
          ClientStatusCodes.UNDOCUMENTED_ERROR
        ).handle();
      }
    } finally {
      setLoading(false);
    }
  };

  return { signOut, loading };
};


export default useAuth;
